import { Card, Row, Col, Statistic, Table, Tag, Progress, Alert } from 'antd';
import { useQuery } from '@tanstack/react-query';
import {
  TeamOutlined,
  CloudServerOutlined,
  DatabaseOutlined,
  CheckCircleOutlined,
  SyncOutlined,
  KeyOutlined,
  WarningOutlined,
  ClockCircleOutlined
} from '@ant-design/icons';
import { monitoringApi } from '../api/monitoring';
import { StatCardSkeleton } from '../components/StatCardSkeleton';
import { TableSkeleton } from '../components/TableSkeleton';

export function Dashboard() {
  const { data: metrics, isLoading, error } = useQuery({
    queryKey: ['dashboard-metrics'],
    queryFn: monitoringApi.getDashboard,
    refetchInterval: 30000, // Refresh every 30 seconds
    refetchIntervalInBackground: false, // Stop refreshing when tab is inactive
  });

  if (isLoading) {
    return (
      <div>
        <h2>Dashboard</h2>
        <StatCardSkeleton count={4} />
        <div style={{ marginTop: 16 }}>
          <StatCardSkeleton count={4} />
        </div>
        <div style={{ marginTop: 16 }}>
          <Card title="Son Senkronizasyonlar">
            <TableSkeleton columns={6} rows={5} />
          </Card>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <Alert
        message="Hata"
        description="Dashboard verileri yüklenirken bir hata oluştu."
        type="error"
        showIcon
      />
    );
  }

  const summary = metrics?.summary;
  const agentStatus = metrics?.agentStatus;
  const recentSyncs = metrics?.recentSyncs || [];

  const syncColumns = [
    {
      title: 'Firma',
      dataIndex: 'firmName',
      key: 'firmName',
    },
    {
      title: 'Veri Kaynağı',
      dataIndex: 'dataSourceName',
      key: 'dataSourceName',
    },
    {
      title: 'Durum',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => {
        const colors: Record<string, string> = {
          Success: 'green',
          Failed: 'red',
          Running: 'blue',
        };
        return <Tag color={colors[status] || 'default'}>{status}</Tag>;
      },
    },
    {
      title: 'Satır',
      dataIndex: 'rowsFetched',
      key: 'rowsFetched',
      render: (rows: number) => rows.toLocaleString('tr-TR'),
    },
    {
      title: 'Süre',
      dataIndex: 'durationMs',
      key: 'durationMs',
      render: (ms: number | null) => ms ? `${(ms / 1000).toFixed(1)}s` : '-',
    },
    {
      title: 'Zaman',
      dataIndex: 'startedAt',
      key: 'startedAt',
      render: (date: string) => new Date(date).toLocaleString('tr-TR'),
    },
  ];

  const onlinePercentage = summary && summary.totalAgents > 0
    ? Math.round((summary.onlineAgents / summary.totalAgents) * 100)
    : 0;

  return (
    <div>
      <h2>Dashboard</h2>

      {/* Main Stats */}
      <Row gutter={[16, 16]}>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Toplam Firma"
              value={summary?.totalFirms || 0}
              prefix={<TeamOutlined />}
            />
          </Card>
        </Col>

        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Online Agent"
              value={summary?.onlineAgents || 0}
              suffix={`/ ${summary?.totalAgents || 0}`}
              prefix={<CloudServerOutlined />}
              valueStyle={{ color: summary?.onlineAgents ? '#3f8600' : '#999' }}
            />
            {summary && summary.totalAgents > 0 && (
              <Progress
                percent={onlinePercentage}
                size="small"
                status={onlinePercentage > 50 ? 'success' : 'exception'}
                showInfo={false}
                style={{ marginTop: 8 }}
              />
            )}
          </Card>
        </Col>

        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Veri Kaynağı"
              value={summary?.totalDataSources || 0}
              prefix={<DatabaseOutlined />}
            />
          </Card>
        </Col>

        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="API Key"
              value={summary?.totalApiKeys || 0}
              prefix={<KeyOutlined />}
            />
          </Card>
        </Col>
      </Row>

      {/* Sync Stats */}
      <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Sync (Son 24 Saat)"
              value={summary?.syncsLast24h || 0}
              prefix={<SyncOutlined />}
            />
          </Card>
        </Col>

        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Başarılı Sync"
              value={summary?.successfulSyncs || 0}
              prefix={<CheckCircleOutlined />}
              valueStyle={{ color: '#3f8600' }}
            />
          </Card>
        </Col>

        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Başarısız Sync"
              value={summary?.failedSyncs || 0}
              prefix={<WarningOutlined />}
              valueStyle={{ color: summary?.failedSyncs ? '#cf1322' : '#999' }}
            />
          </Card>
        </Col>

        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Başarı Oranı"
              value={summary?.syncSuccessRate || 100}
              suffix="%"
              prefix={<ClockCircleOutlined />}
              valueStyle={{ color: (summary?.syncSuccessRate || 100) >= 90 ? '#3f8600' : '#faad14' }}
            />
          </Card>
        </Col>
      </Row>

      {/* Additional Stats */}
      <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
        <Col xs={24} sm={12}>
          <Card>
            <Statistic
              title="Toplam Senkronize Edilen Satır (24 Saat)"
              value={summary?.totalRowsSynced || 0}
              groupSeparator="."
            />
          </Card>
        </Col>

        <Col xs={24} sm={12}>
          <Card>
            <Statistic
              title="Hata Logları (24 Saat)"
              value={summary?.errorLogsLast24h || 0}
              prefix={<WarningOutlined />}
              valueStyle={{ color: summary?.errorLogsLast24h ? '#cf1322' : '#52c41a' }}
            />
          </Card>
        </Col>
      </Row>

      {/* Recent Syncs */}
      <Card title="Son Senkronizasyonlar" style={{ marginTop: 16 }}>
        <Table
          dataSource={recentSyncs}
          columns={syncColumns}
          rowKey="id"
          pagination={false}
          size="small"
          locale={{ emptyText: 'Henüz senkronizasyon yok' }}
        />
      </Card>

      {/* Agent Status Distribution */}
      {agentStatus && (agentStatus.online > 0 || agentStatus.offline > 0) && (
        <Card title="Agent Durumu" style={{ marginTop: 16 }}>
          <Row gutter={16}>
            <Col span={12}>
              <Statistic
                title="Online"
                value={agentStatus.online}
                valueStyle={{ color: '#3f8600' }}
              />
            </Col>
            <Col span={12}>
              <Statistic
                title="Offline"
                value={agentStatus.offline}
                valueStyle={{ color: '#999' }}
              />
            </Col>
          </Row>
        </Card>
      )}
    </div>
  );
}
